import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../api";
import Topbar from "./Topbar";
import BackLink from "./BackLink";
import NewLeadModal from "./NewLeadModal";
import ContactDetailPage from "./ContactDetailPage";
import useEscapeLayer from "./useEscape";
import ChangeStatusMenu from "./record/ChangeStatusMenu";
import ConfirmCard from "./record/ConfirmCard";
import MergeCard from "./MergeCard";
import { statusGroups, leadsStatusOptions, leadsConfirmSpec, leadsWrites } from "./leadsStatus.mjs";
import {
  STAGE_PILLS, CHANNEL_LABELS, COMPANY_STATUS_LABELS, BOARD_STAGE_LABELS,
  stagePill, shownStage, sourcePill, contactName, ago, dateShort, moneyK, companyExitTag, moneyMeaningTitle,
  leadChannel, leadChannelTitle, signedFeeLines, agreementLink,
} from "./ui";
import {
  LEAD_COLUMNS, LEAD_COLUMN_GROUPS, LEAD_FILTER_BY_KEY, PRIMARY_LEAD_FILTERS, SECONDARY_LEAD_FILTERS,
  MAX_VIEWS, MAX_VIEW_NAME, activeLeadFilters, leadFilterValues, toggleLeadFilterValue,
  normalizeLeadView, defaultLeadViewState, sameLeadViewState, leadViewId, blankLeadFilters,
} from "./leadViews.mjs";

// One page of matched leads. The table is filtered and counted in SQL, so this
// is a rendering budget, not the set the filters ran against.
const PAGE = 500;

// A filter change is a round trip, and a search box is one round trip per
// keystroke without this. Long enough to coalesce typing, short enough that a
// select still feels immediate.
const FILTER_DEBOUNCE_MS = 250;

// Only the lifecycle stages a lead actually passes through are offered as
// filters / bulk targets; subscriber, evangelist and other never apply here.
const STAGE_OPTIONS = Object.keys(STAGE_PILLS);
// How many companies Change status… reads at once while it opens on a selection.
const STATUS_FETCH_AT_ONCE = 6;

/**
 * The table, its filter band and its saved views are all one config
 * (leadViews.mjs): columns are keys, filters are keys, and a view is
 * { filters, columns, sort } persisted in settings.lead_views. Nothing below
 * hardcodes a column or a filter — adding either is an entry in that file plus,
 * for a column, its cell here.
 */

// A HubSpot property off the contact's snapshot, or "" — the blob stores
// strings, and a blank string is the same unknown as a missing key.
const prop = (c, key) => {
  const v = c.properties && c.properties[key];
  return v == null ? "" : String(v).trim();
};
const selfReported = (c, key) => {
  const v = c.properties && c.properties.self_reported && c.properties.self_reported[key];
  return v == null ? "" : String(v).trim();
};

// A link cell. The row opens the record, so the link has to stop the click or
// it becomes a way to lose your place.
const cellLink = (href, text, title) => (href
  ? <a className="crm-cell__link" href={href} target="_blank" rel="noreferrer" title={title} onClick={(e) => e.stopPropagation()}>{text}</a>
  : "—");

const httpUrl = (u) => (!u ? null : /^https?:\/\//i.test(u) ? u : `https://${u.replace(/^\/+/, "")}`);

// Long prose (a description, a tech stack) is cut to one short line; the whole
// value is the tooltip.
const clipped = (text, n = 60) => {
  const s = String(text || "").trim();
  if (!s) return "—";
  return s.length > n ? <span title={s}>{s.slice(0, n - 1)}…</span> : s;
};

const humanize = (v) => (v ? String(v).replace(/_/g, " ") : "—");

// "$50M", "$1.2B" — annual revenue arrives as a raw USD figure.
function bigMoney(raw) {
  const n = Number(raw);
  if (!raw || !Number.isFinite(n) || n <= 0) return "—";
  if (n >= 1e9) return `$${+(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${+(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${n}`;
}

// The HubSpot form's "running LinkedIn ads?" answer. 465 of the stored values
// are the QUESTION itself (the field echoed its label), and a handful are in
// the lead's own language; both are normalised so the column reads Yes / No.
function liAdsAnswer(c) {
  const v = prop(c, "are_you_currently_running_linkedin_ads");
  if (!v || v.includes("?") || v.includes("\u061F")) return "—";
  if (/^(yes|oui|s[ií]|sim|ja)$/i.test(v)) return "Yes";
  if (/^(no|non|n[aã]o|nein)$/i.test(v)) return "No";
  return v;
}

// Column key -> how the cell renders. `td` carries the cell's own classes;
// crm-num is tabular figures and belongs on dates, money and counts only.
const CELLS = {
  name: { td: "crm-td--name", render: (c) => contactName(c) },
  company: {
    // The audited tag rides on the company, because the audit is the company's:
    // every lead at an audited account reads the same.
    render: (c) => (c.company_name
      ? (
        <>
          {c.company_name}
          {c.company_audited_at && (
            <>
              {" "}
              <span className="crm-tag crm-tag--audited" title={`LinkedIn audit · ${dateShort(c.company_audited_at)}`}>audited</span>
            </>
          )}
        </>
      )
      : "—"),
  },
  job_title: { td: "crm-td--muted", render: (c) => c.job_title || "—" },
  email: { td: "crm-td--muted", render: (c) => c.email || "—" },
  phone: { td: "crm-td--muted crm-num", render: (c) => c.phone || "—" },
  linkedin: {
    render: (c) => (c.linkedin_url
      // The row opens the contact, so the one link inside it has to stop the
      // click or it becomes a way to lose your place.
      ? <a className="crm-cell__link" href={c.linkedin_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>profile</a>
      : "—"),
  },
  li_connected: {
    td: "crm-td--muted crm-num",
    render: (c) => (c.linkedin_connected_at
      ? <span title={c.linkedin_connection_match === "name" ? "Matched by name and company: no LinkedIn URL on file" : c.linkedin_connection_match === "hand" ? "Marked “Already connected” by you on the record" : undefined}>{dateShort(c.linkedin_connected_at)}</span>
      : "—"),
  },
  stage: {
    // Disqualified / lost replaces the stage pill with a neutral tag — it
    // describes a completed action, not a warning.
    render: (c) => {
      const exit = companyExitTag(c);
      if (exit) return <span className="crm-tag crm-tag--disqualified">{exit}</span>;
      const stage = stagePill(shownStage(c.lifecycle_stage, c.company_status));
      return <span className={`crm-pill ${stage.cls}`}>{stage.label}</span>;
    },
  },
  channel: {
    // leadChannel(), not c.lead_channel: 1,072 migrated leads have their channel
    // derived from the form they submitted, and the filter matches the same
    // expression. The tooltip says which of the two this row is.
    render: (c) => {
      const channel = leadChannel(c);
      return channel
        ? (
          <span className="crm-pill crm-pill--channel" title={leadChannelTitle(c)}>
            {CHANNEL_LABELS[channel] || channel}
          </span>
        )
        : "—";
    },
  },
  source: {
    render: (c) => {
      const src = sourcePill(c.lead_source);
      return src ? <span className={`crm-pill ${src.cls}`}>{src.label}</span> : "—";
    },
  },
  touch_detail: { td: "crm-td--muted", render: (c) => c.first_touch_detail || "—" },
  // Company status and pipeline stage render as plain text, not pills. There are
  // exactly three pill families and they are locked; a fourth in the same row as
  // the stage pill would read as a competing lifecycle.
  company_status: { render: (c) => COMPANY_STATUS_LABELS[c.company_status] || c.company_status || "—" },
  pipeline_stage: { render: (c) => BOARD_STAGE_LABELS[c.company_pipeline_stage] || "—" },
  value: {
    td: "crm-num",
    render: (c) => (c.company_monthly_value == null
      ? "—"
      : <span title={moneyMeaningTitle(c.company_status)}>{moneyK(c.company_monthly_value, c.company_currency)}</span>),
  },
  industry: { td: "crm-td--muted", render: (c) => c.company_industry || "—" },
  employees: { td: "crm-td--muted crm-num", render: (c) => c.company_employee_range || "—" },
  country: { td: "crm-td--muted", render: (c) => c.company_country || "—" },
  mobile: { td: "crm-td--muted crm-num", render: (c) => prop(c, "mobilephone") || "—" },
  person_location: {
    td: "crm-td--muted",
    render: (c) => [prop(c, "city"), prop(c, "state"), prop(c, "country")].filter(Boolean).join(", ") || "—",
  },
  replied: { td: "crm-td--muted", render: (c) => (c.replied ? "Yes" : "No") },
  lead_status: { td: "crm-td--muted", render: (c) => humanize(c.lead_status) },
  status_reason: { td: "crm-td--muted", render: (c) => humanize(c.company_status_reason) },
  domain: { td: "crm-td--muted", render: (c) => c.company_domain || "—" },
  company_linkedin: { render: (c) => cellLink(httpUrl(c.company_linkedin_url), "page") },
  recycle_until: { td: "crm-td--muted crm-num", render: (c) => dateShort(c.company_recycle_until) },
  re_engage_after: { td: "crm-td--muted crm-num", render: (c) => dateShort(c.company_re_engage_after) },
  won: { td: "crm-td--muted crm-num", render: (c) => dateShort(c.company_won_at) },
  churned: { td: "crm-td--muted crm-num", render: (c) => dateShort(c.company_churned_at) },
  // The fee is a structure, several lines of one quote (signedFeeLines); the
  // cell shows the first and the tooltip has them all.
  signed_fee: {
    td: "crm-td--muted",
    render: (c) => {
      const lines = signedFeeLines(c.company_signed_fee_structure);
      if (!lines.length) return "—";
      return <span title={lines.join("\n")}>{lines[0]}{lines.length > 1 ? ` +${lines.length - 1}` : ""}</span>;
    },
  },
  signed_terms: { td: "crm-td--muted", render: (c) => clipped(c.company_signed_terms_note) },
  agreement: {
    render: (c) => {
      const link = agreementLink({ signed_agreement_url: c.company_signed_agreement_url });
      return link ? cellLink(link.url, "open", link.label) : "—";
    },
  },
  audited: {
    td: "crm-td--muted crm-num",
    render: (c) => (c.company_audited_at
      ? <span title={c.company_audit_notes || undefined}>{dateShort(c.company_audited_at)}</span>
      : "—"),
  },
  company_last_activity: { td: "crm-td--muted crm-num", render: (c) => ago(c.company_last_activity_at) },
  company_created: { td: "crm-td--muted crm-num", render: (c) => dateShort(c.company_created_at) },
  company_city: {
    td: "crm-td--muted",
    render: (c) => [c.company_city, c.company_state].filter(Boolean).join(", ") || "—",
  },
  timezone: { td: "crm-td--muted", render: (c) => c.company_timezone || "—" },
  founded: { td: "crm-td--muted crm-num", render: (c) => c.company_founded_year || "—" },
  revenue: { td: "crm-td--muted crm-num", render: (c) => bigMoney(c.company_annual_revenue) },
  // Already formatted by the enrichment ("$10.5M"), or a raw figure from
  // Apollo's total_funding.
  funding: {
    td: "crm-td--muted crm-num",
    render: (c) => (/^[0-9.]+$/.test(c.company_funding || "") ? bigMoney(c.company_funding) : (c.company_funding || "—")),
  },
  description: { td: "crm-td--muted", render: (c) => clipped(c.company_description) },
  tech: { td: "crm-td--muted", render: (c) => clipped((c.company_tech || "").replace(/;/g, ", ").replace(/_/g, " ")) },
  li_ads: { td: "crm-td--muted", render: liAdsAnswer },
  li_budget: {
    td: "crm-td--muted",
    render: (c) => prop(c, "what_is_your_monthly_budget_for_linkedin_ads")
      || prop(c, "monthly_ads_budget") || prop(c, "monthly_ad_spend") || "—",
  },
  heard_about: {
    td: "crm-td--muted",
    render: (c) => clipped(selfReported(c, "hear_about_us") || prop(c, "how_did_you_hear_about_us_"), 40),
  },
  referred_by: {
    td: "crm-td--muted",
    render: (c) => {
      const v = selfReported(c, "referred_by") || prop(c, "referred_by");
      // The self-reported "were you referred?" box stores a literal No.
      return /^no$/i.test(v) ? "—" : clipped(v, 40);
    },
  },
  // "Get a Proposal to Work with Remotion: #wf-form-Get-a-Proposal .contact_form"
  // — the page title before the selector is the part a person reads.
  first_form: {
    td: "crm-td--muted",
    render: (c) => {
      const v = prop(c, "first_conversion_event_name");
      if (!v) return "—";
      return <span title={v}>{v.split(/: #| : /)[0]}</span>;
    },
  },
  first_conversion: { td: "crm-td--muted crm-num", render: (c) => dateShort(prop(c, "first_conversion_date")) },
  intro_call_set: { td: "crm-td--muted crm-num", render: (c) => dateShort(prop(c, "intro_call_set_date")) },
  last_activity: { td: "crm-td--muted crm-num", render: (c) => ago(c.last_activity_at) },
  created: { td: "crm-td--muted crm-num", render: (c) => dateShort(c.created_at) },
  updated: { td: "crm-td--muted crm-num", render: (c) => dateShort(c.updated_at) },
};

// "1 filter", "2 filters" — a saved view can hold exactly one of either.
const countOf = (n, noun) => `${n} ${noun}${n === 1 ? "" : "s"}`;

// Filter values are labelled from the pill vocabularies in ui.js rather than
// re-spelled here, so a filter option and the cell it matches always say the
// same word.
const VALUE_LABELS = {
  stage: (v) => stagePill(v).label,
  channel: (v) => CHANNEL_LABELS[v] || v,
  source: (v) => sourcePill(v)?.label || v,
  company_status: (v) => COMPANY_STATUS_LABELS[v] || v,
  pipeline_stage: (v) => BOARD_STAGE_LABELS[v] || v,
};

// The options a filter offers. Open filters (industry, country) take theirs from
// the server's facets — every value attached to a lead — so no option is a dead
// end and the list does not shrink as other filters narrow the page. `current`
// is kept in the list even when the facets no longer contain it, or a saved view
// would apply a filter its own select could not show.
function filterOptions(def, facets, current = []) {
  if (def.open) {
    const values = [...new Set([...(facets?.[def.key] || []), ...current])];
    return values.sort((a, b) => a.localeCompare(b)).map((v) => [v, v]);
  }
  if (def.options) return def.options;
  const label = VALUE_LABELS[def.labels] || ((v) => v);
  return (def.values || []).map((v) => [v, label(v)]);
}

// What a set of ticked values is called. Two are named; beyond that the count
// is more use than a truncated list, and the chip's tooltip has all of them.
function valuesLabel(def, values, facets) {
  const options = filterOptions(def, facets, values);
  const label = (v) => (options.find(([o]) => o === v) || [v, v])[1];
  if (!values.length) return def.all;
  if (values.length <= 2) return values.map(label).join(", ");
  return `${label(values[0])} +${values.length - 1}`;
}

const valuesTitle = (def, values, facets) => {
  const options = filterOptions(def, facets, values);
  return values.map((v) => (options.find(([o]) => o === v) || [v, v])[1]).join(", ");
};

/**
 * A filter that takes several values: a button carrying the current selection
 * and a popover of checkboxes.
 *
 * Not a native <select multiple>, which needs ctrl-click to do the thing it is
 * for and cannot say "Stage · 2" when closed — this control has to read as a
 * filter that is ON at a glance, from the same row as the single-value selects
 * next to it.
 */
function MultiFilter({ def, values, facets, open, onToggleOpen, onPick, onClear }) {
  const options = filterOptions(def, facets, values);
  return (
    <span className="crm-anchor">
      <button
        type="button"
        className={`crm-multi${values.length ? " is-on" : ""}`}
        onClick={onToggleOpen}
        title={values.length ? `${def.label}: ${valuesTitle(def, values, facets)}` : def.all}
      >
        {valuesLabel(def, values, facets)}
        <span className="crm-multi__caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="crm-pop crm-pop--check">
          <h4>{def.label}</h4>
          <div className="crm-checklist">
            {options.map(([v, l]) => (
              <label key={v} className="crm-check">
                <input type="checkbox" checked={values.includes(v)} onChange={() => onPick(v)} />
                <span>{l}</span>
              </label>
            ))}
            {!options.length && <div className="crm-hint">No values yet.</div>}
          </div>
          <div className="crm-pop__divider crm-pop__menu">
            <button className="crm-linkact" disabled={!values.length} onClick={onClear}>Clear</button>
            <button className="crm-btn crm-btn--secondary" onClick={onToggleOpen}>Done</button>
          </div>
        </div>
      )}
    </span>
  );
}

// Drag grip, same mark as the Reports module cards.
function Grip(props) {
  return (
    <span className="crm-colpick__grip" title="Drag to reorder" draggable {...props}>
      <svg viewBox="0 0 6 16" aria-hidden="true">
        {[2.5, 8, 13.5].map((y) => (
          <g key={y}><circle cx="1.4" cy={y} r="1.2" /><circle cx="4.6" cy={y} r="1.2" /></g>
        ))}
      </svg>
    </span>
  );
}

// `/leads?ever_stage=proposal_sent` — what a board column head links to. Only
// a value the filter knows; anything else opens the list as usual.
function linkedEverStage(search) {
  const value = new URLSearchParams(search).get("ever_stage");
  return LEAD_FILTER_BY_KEY.get("ever_stage").values.includes(value) ? value : null;
}

export default function LeadsListPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  // matched = leads passing the filters, total = leads. Both come from SQL over
  // the whole table; rows is one page of the matched set.
  const [counts, setCounts] = useState({ matched: 0, total: 0 });
  const [facets, setFacets] = useState({ industry: [], country: [] });
  // What the table is showing right now: filters + columns + sort. This is
  // exactly what a saved view stores, which is what makes "edited" answerable.
  const [state, setState] = useState(defaultLeadViewState);
  const [views, setViews] = useState([]);
  const [activeId, setActiveId] = useState(null);
  // Settings arrive before the rows do, so the first fetch already uses the
  // opening view's filters and sort instead of loading a page of the wrong 500.
  const [booted, setBooted] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [panel, setPanel] = useState(null); // "filters" | "columns" | "save"
  const [newName, setNewName] = useState("");
  const [drag, setDrag] = useState({ from: null, over: null });
  const [colQuery, setColQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  // Change status…, open on a row (its id) or the selection ("bulk"): the rows it
  // acts on, and each one's company and actions once they have loaded (bundles,
  // null until then). `ending` is the confirm open over the page, picked from it.
  const [status, setStatus] = useState(null); // { key, targets, bundles }
  const [ending, setEnding] = useState(null); // { id, preset }
  // Merge…: the confirm's rows and the one it opens on (POST /contacts/merge-preview).
  const [merging, setMerging] = useState(null);
  const statusReq = useRef(0);
  const statusRef = useRef(null);
  // The ignore list, so the page can say what "not a lead" has taken out and
  // offer the way back. Fetched once with the first page.
  const [ignored, setIgnored] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  // The record open beside the table. A contact id, never a route: the list is
  // the triage surface and its filters, page and scroll are expensive to rebuild.
  const [peekId, setPeekId] = useState(null);
  // The open record's own guard (ContactDetailPage fills it in): another row, or
  // Esc from out here, would unmount it and an edited reply inside it, so that
  // draft asks first.
  const recordGuard = useRef(null);
  const leaveRecord = useCallback((proceed) => (recordGuard.current ? recordGuard.current(proceed) : proceed()), []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const { filters, columns, sort } = state;
  const patch = (changes) => setState((s) => ({ ...s, ...changes }));

  // The filters the QUERY uses, a beat behind the ones the band shows. Typing
  // stays instant; the round trip waits for you to stop.
  const [debounced, setDebounced] = useState(filters);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(filters), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filters]);

  /**
   * Applying a whole view is not typing, so it skips the debounce: otherwise
   * opening the page on a saved view fetched the UNFILTERED page first and
   * replaced it 250ms later — one wasted query for every lead in the table, and
   * a flash of rows the view says to hide.
   *
   * The effect above still fires afterwards, with the same object reference this
   * sets, so React bails out and nothing re-fetches.
   */
  function applyViewState(next) {
    setState(next);
    setDebounced(next.filters);
  }

  useEffect(() => {
    (async () => {
      try {
        const s = await api("/settings");
        const list = (Array.isArray(s.lead_views) ? s.lead_views : []).map(normalizeLeadView);
        setViews(list);
        const opening = list.find((v) => v.id === s.lead_view_active);
        const everStage = linkedEverStage(location.search);
        if (everStage) {
          // Arrived from a board column head: that question, and only that one.
          // The default view plus the filter, not the saved view's filters on
          // top of it — "ever in Pricing sent" with a hidden "created 30d" still
          // applied would be a wrong answer that looks like the right one. The
          // saved view is untouched (nothing is written), and the query string
          // is dropped so a reload is the ordinary list again. The route state
          // survives the replace: it is what draws the way back to the board.
          const base = defaultLeadViewState();
          const shown = new Set([...base.columns, "company_status", "pipeline_stage"]);
          applyViewState({
            ...base,
            filters: { ...blankLeadFilters(), ever_stage: [everStage] },
            columns: LEAD_COLUMNS.map((c) => c.key).filter((k) => shown.has(k)),
          });
          navigate(location.pathname, { replace: true, state: location.state });
        } else if (opening) {
          setActiveId(opening.id);
          setState({ filters: opening.filters, columns: opening.columns, sort: opening.sort });
          setDebounced(opening.filters);
        }
      } catch (e) {
        // A views list that failed to load must not cost you the leads table.
        setError(e.message);
      } finally {
        setBooted(true);
      }
    })();
  }, []);

  // A suppression list nobody can see is a list that quietly decides what the
  // CRM never shows — the same reason every active filter renders as a chip. It
  // loads alongside the page and its button only appears when it holds
  // something, so an empty list costs no attention.
  const loadIgnored = useCallback(async () => {
    try {
      const r = await api("/ignored-senders");
      setIgnored(r.senders || []);
    } catch {
      // The ignore list failing to load must not cost you the leads table.
      setIgnored([]);
    }
  }, []);

  useEffect(() => { loadIgnored(); }, [loadIgnored]);

  // Filtering, sorting and counting all happen in SQL over every lead. What
  // arrives here is one page of the matched set plus the two numbers in the
  // header, so "23 of 1,560" is a claim about the table and not about the page.
  const query = useMemo(() => {
    const params = new URLSearchParams({
      kind: "lead", limit: String(PAGE), sort: sort.key, dir: sort.dir, stats: "1", props: "lead",
    });
    // One entry per value, `f[key][]`, which qs parses back into an array. A
    // comma-joined list would have been shorter and wrong: an industry or a
    // country is free text and some of them contain commas.
    for (const [key, values] of activeLeadFilters(debounced)) {
      for (const value of values) params.append(`f[${key}][]`, value);
    }
    return params;
  }, [debounced, sort.key, sort.dir]);

  // Every filter keystroke is a query, so the newest answer has to win: an
  // in-flight request for a filter set you have already changed must not land on
  // top of the current one.
  const reqRef = useRef(0);

  const fetchPage = useCallback(async (offset, { keepSelection = false, size = PAGE } = {}) => {
    if (!booted) return;
    const params = new URLSearchParams(query);
    params.set("offset", String(offset));
    params.set("limit", String(size));
    const req = ++reqRef.current;
    try {
      const r = await api(`/contacts?${params}`);
      if (req !== reqRef.current) return;
      setRows((prev) => (offset && prev ? [...prev, ...r.contacts] : r.contacts));
      setCounts({ matched: r.matched ?? r.contacts.length, total: r.total ?? r.contacts.length });
      if (r.facets) setFacets(r.facets);
      setError("");
      if (!offset && !keepSelection) {
        // A new filter set is a new list: a selection made against the old one
        // would act on rows that are no longer in front of you. Appending a page
        // keeps it, and so does a refresh that only picks up what the record
        // panel changed.
        setSelected(new Set());
        setStatus(null);
        setEnding(null);
      }
    } catch (e) {
      if (req === reqRef.current) setError(e.message);
    }
  }, [booted, query]);

  useEffect(() => { fetchPage(0); }, [fetchPage]);

  // A stage change, a note, a send or a disqualification made in the panel is a
  // change to the row behind it, so the list refreshes, keeping the checkbox
  // selection, which the panel never touched. Refreshing to page one would throw
  // away the pages you had loaded, so it asks for as many rows as are on screen.
  const refreshRows = useCallback(
    () => fetchPage(0, { keepSelection: true, size: Math.max(PAGE, (rows || []).length) }),
    [fetchPage, rows]
  );
  const closePeek = useCallback(() => {
    setPeekId(null);
    refreshRows();
  }, [refreshRows]);

  // Escape closes the record, the same way its × does. A layer (useEscape.js), so
  // it never fires while typing in the panel's composer, and anything opened
  // inside the panel closes first.
  useEscapeLayer(!!peekId, () => leaveRecord(closePeek));

  // Reload the current page-0 view after a write (stage change, disqualify).
  const reload = useCallback(() => fetchPage(0), [fetchPage]);

  async function loadMore() {
    setLoadingMore(true);
    try { await fetchPage(rows.length); }
    finally { setLoadingMore(false); }
  }

  // First click on a new column sorts descending (most recent / highest first,
  // which is what you want for dates and stages); clicking the active column
  // flips direction.
  function toggleSort(key) {
    patch({ sort: sort.key === key ? { key, dir: sort.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" } });
  }

  const setFilter = (key, value) => patch({ filters: { ...filters, [key]: value } });
  const pickFilter = (key, value) => patch({ filters: toggleLeadFilterValue(filters, key, value) });
  const clearFilter = (key) => patch({ filters: { ...filters, [key]: LEAD_FILTER_BY_KEY.get(key)?.multi ? [] : "" } });

  // --- Columns

  function toggleColumn(key) {
    if (columns.includes(key)) {
      // A table with no columns is not a state worth being able to reach.
      if (columns.length === 1) return;
      return patch({ columns: columns.filter((k) => k !== key) });
    }
    // Re-added columns land where LEAD_COLUMNS says they belong relative to the
    // columns already shown, rather than at the end — but a hand-dragged order is
    // never re-sorted, because that would undo the drag on the next add.
    const order = LEAD_COLUMNS.map((c) => c.key);
    const idx = order.indexOf(key);
    const at = columns.findIndex((k) => order.indexOf(k) > idx);
    const next = columns.slice();
    next.splice(at === -1 ? columns.length : at, 0, key);
    patch({ columns: next });
  }

  function dropColumn(targetKey) {
    const from = columns.indexOf(drag.from);
    const to = columns.indexOf(targetKey);
    setDrag({ from: null, over: null });
    if (from < 0 || to < 0 || from === to) return;
    const next = columns.slice();
    next.splice(to, 0, next.splice(from, 1)[0]);
    patch({ columns: next });
  }

  // --- Views. One PUT writes the whole list, the way Reports writes its modules;
  // the active pointer is its own setting so switching views is one small write.

  const activeView = views.find((v) => v.id === activeId) || null;
  const baseline = activeView
    ? { filters: activeView.filters, columns: activeView.columns, sort: activeView.sort }
    : defaultLeadViewState();
  const edited = !sameLeadViewState(state, baseline);

  async function saveViews(next, nextActive) {
    const previous = { views, activeId };
    setViews(next);
    if (nextActive !== undefined) setActiveId(nextActive);
    setError("");
    try {
      const body = { lead_views: next };
      if (nextActive !== undefined) body.lead_view_active = nextActive;
      await api("/settings", { method: "PUT", body: JSON.stringify(body) });
    } catch (e) {
      setError(e.message);
      setViews(previous.views);
      setActiveId(previous.activeId);
    }
  }

  // Which view the list opens on is persisted rather than kept in component
  // state: a view you have to re-pick on every visit is barely a saved view.
  async function openView(view) {
    setPanel(null);
    setActiveId(view ? view.id : null);
    applyViewState(view
      ? { filters: view.filters, columns: view.columns, sort: view.sort }
      : defaultLeadViewState());
    try {
      await api("/settings", { method: "PUT", body: JSON.stringify({ lead_view_active: view ? view.id : null }) });
    } catch (e) { setError(e.message); }
  }

  function createView() {
    const name = newName.trim();
    if (!name) return;
    const view = { id: leadViewId(name, views.map((v) => v.id)), name, filters, columns, sort };
    setPanel(null);
    setNewName("");
    saveViews([...views, view], view.id);
  }

  function updateView() {
    saveViews(views.map((v) => (v.id === activeId ? { ...v, filters, columns, sort } : v)));
  }

  function deleteView() {
    if (!activeView) return;
    if (!window.confirm(`Delete the view “${activeView.name}”? The leads are untouched.`)) return;
    applyViewState(defaultLeadViewState());
    saveViews(views.filter((v) => v.id !== activeId), null);
  }

  // --- Row / bulk actions. Each resolves against the selected contacts and
  // reloads, since every one of them changes derived row state.

  async function run(fn) {
    setBusy(true);
    setStatus(null);
    try { await fn(); await reload(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const changeStage = (targets, stage) => run(async () => {
    for (const c of targets) {
      await api(`/contacts/${c.id}`, { method: "PATCH", body: JSON.stringify({ lifecycle_stage: stage }) });
    }
  });

  // Junk stays one click: the record is kept for repeat-spam recognition, and a
  // junked sole contact takes its empty company out as not_a_fit / 'junk'.
  const markJunk = (targets) => run(async () => {
    for (const c of targets) await api(`/contacts/${c.id}/junk`, { method: "POST" });
  });

  /**
   * CHANGE STATUS…: the record's own menu and confirm (record/ChangeStatusMenu,
   * record/ConfirmCard) behind the row's ⋯ and the bulk bar, with leadsStatus.mjs
   * deciding what they offer and send. Opening it reads each company and its
   * actions, because what fits is the record's call (the lost gate, the open
   * lines in Next), not something the row knows.
   */
  async function openStatus(key, targets) {
    if (status && status.key === key) { closeStatus(); return; }
    const req = ++statusReq.current;
    setEnding(null);
    setStatus({ key, targets, bundles: null });
    try {
      const groups = statusGroups(targets);
      const bundles = new Array(groups.length);
      for (let i = 0; i < groups.length; i += STATUS_FETCH_AT_ONCE) {
        await Promise.all(groups.slice(i, i + STATUS_FETCH_AT_ONCE).map(async (g, j) => {
          const [company, act] = g.companyId
            ? await Promise.all([api(`/companies/${g.companyId}`), api(`/companies/${g.companyId}/actions`).catch(() => null)])
            : [null, null];
          bundles[i + j] = { company, act, contacts: g.contacts };
        }));
        if (req !== statusReq.current) return;
      }
      setStatus({ key, targets, bundles });
    } catch (e) {
      if (req !== statusReq.current) return;
      setStatus(null);
      setError(e.message);
    }
  }

  function closeStatus() {
    statusReq.current += 1;
    setStatus(null);
    setEnding(null);
  }

  // Delete… needs no company: it is about the rows themselves.
  function openDelete(targets) {
    statusReq.current += 1;
    setStatus({ key: "bulk", targets, bundles: statusGroups(targets).map((g) => ({ company: null, act: null, contacts: g.contacts })) });
    setEnding({ id: "delete", preset: {} });
  }

  // Merge… asks the server which rows these are and which one to keep, then
  // folds the rest into the one he picks (src/dedupe/mergeContacts).
  async function openMerge(targets) {
    closeStatus();
    setBusy(true);
    try {
      setMerging(await api("/contacts/merge-preview", { method: "POST", body: JSON.stringify({ ids: targets.map((c) => c.id) }) }));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function applyMerge(keep) {
    const fold = merging.rows.map((r) => r.id).filter((id) => id !== keep);
    await api("/contacts/merge", { method: "POST", body: JSON.stringify({ keep, fold }) });
    if (fold.includes(peekId)) setPeekId(null);
    setMerging(null);
    setSelected(new Set());
    await reload();
  }

  /*
   * The confirmed option, company by company. One that fails does not stop the
   * rest; the page then says which, since the others have already landed. Only
   * when nothing landed does the error stay on the confirm, beside its button.
   */
  async function applyStatus(id, choice) {
    const { runs } = leadsWrites(id, choice, status.bundles, { now: new Date() });
    const failed = [];
    for (const r of runs) {
      try {
        for (const w of r.writes) {
          await api(w.path, { method: w.method, body: w.body === undefined ? undefined : JSON.stringify(w.body) });
        }
      } catch (e) {
        failed.push(`${r.name}: ${e.message}`);
      }
    }
    if (runs.length && failed.length === runs.length) throw new Error(failed.join(" · "));
    // A deleted person cannot stay open beside the list.
    if ((id === "delete" || id === "not_a_lead") && status.targets.some((c) => c.id === peekId)) setPeekId(null);
    if (id === "not_a_lead") await loadIgnored();
    setStatus(null);
    setEnding(null);
    await reload();
    if (failed.length) setError(`Done for ${runs.length - failed.length} of ${runs.length}. Not done: ${failed.join(" · ")}`);
  }

  const statusOps = {
    closeStatus,
    openEnding: (id, preset = {}) => {
      // Mark junk writes at once, as it always has; everything else confirms.
      if (id === "junk") return markJunk(status.targets);
      setEnding({ id, preset });
    },
  };

  // The confirm's words, worked out once per opening so its "now" holds still.
  const endingSpec = useMemo(
    () => (ending && status && status.bundles ? leadsConfirmSpec(ending.id, status.bundles, { now: new Date() }) : null),
    [ending, status]
  );

  // The menu closes on a click anywhere else, as the record's does; Esc is its
  // own layer (ChangeStatusMenu).
  useEffect(() => {
    if (!status || ending) return undefined;
    const onDown = (e) => {
      if (statusRef.current && !statusRef.current.contains(e.target)) closeStatus();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [status, ending]);

  const unignore = (email) => run(async () => {
    await api(`/ignored-senders/${encodeURIComponent(email)}`, { method: "DELETE" });
    await loadIgnored();
  });

  // --- Selection

  // rows is null until the first page lands, and everything here runs before the
  // loading branch below.
  const loaded = rows || [];
  const selectedRows = loaded.filter((c) => selected.has(c.id));
  const allShownSelected = loaded.length > 0 && loaded.every((c) => selected.has(c.id));

  function toggle(id) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  }
  function toggleAll() {
    setSelected(allShownSelected ? new Set() : new Set(loaded.map((c) => c.id)));
  }

  const togglePanel = (name) => setPanel(panel === name ? null : name);

  // The menu itself, wherever it opened: a line while the companies load, then
  // the record's Change status… with the Leads page's options.
  const statusMenu = (key) =>
    status && status.key === key && !ending ? (
      <div className="rec crm-statuspop" onClick={(e) => e.stopPropagation()}>
        {status.bundles ? (
          <ChangeStatusMenu record={{ layout: "desktop" }} ops={statusOps} sections={leadsStatusOptions(status.bundles)} />
        ) : (
          <div className="rec-menu"><div className="rec-menu__label">Reading {status.targets.length === 1 ? "the record" : "the records"}…</div></div>
        )}
      </div>
    ) : null;

  // The columns actually rendered, in the view's order, each with its header
  // label, its sort key and its cell.
  const shown = columns.map((key) => ({ ...LEAD_COLUMNS.find((c) => c.key === key), cell: CELLS[key] }))
    .filter((c) => c.key && c.cell);

  // The picker's "add" half: every column not shown, narrowed by its search box.
  const addable = LEAD_COLUMNS.filter((c) => !columns.includes(c.key));
  const colNeedle = colQuery.trim().toLowerCase();
  const matching = colNeedle ? addable.filter((c) => c.label.toLowerCase().includes(colNeedle)) : addable;

  const extras = activeLeadFilters(filters).filter(([k]) => !LEAD_FILTER_BY_KEY.get(k)?.primary);

  if (!rows) {
    return <><Topbar title="Leads" /><div className="crm-leads">{error ? <p className="crm-login__err">{error}</p> : "Loading…"}</div></>;
  }

  return (
    <>
      {/* matched of total, both counted in SQL. When the page is smaller than
          the match, the pager below the table says so — the header never
          quietly reports the size of the page as the size of the answer. */}
      {/* The way back only when a board column head sent you here: that jump
          is a question asked from the Pipeline, and the answer should hand you
          back to it. In the sticky bar so it survives scrolling. Opened from
          the nav, Leads is a top-level page with nothing behind it. */}
      <Topbar
        back={location.state && location.state.from ? <BackLink /> : null}
        title="Leads"
        sub={`${counts.matched} of ${counts.total} leads`}>
        <button className="crm-btn crm-btn--primary" onClick={() => setShowAdd(true)}>New lead</button>
      </Topbar>

      {/* Saved views: the filter set, the columns and the sort under one name.
          Its own strip above the filters, because picking a view REPLACES
          everything in the band below it. */}
      <div className="crm-views">
        <button className={`crm-viewchip${activeId ? "" : " is-on"}`} onClick={() => openView(null)}>All leads</button>
        {views.map((v) => (
          <button
            key={v.id}
            className={`crm-viewchip${activeId === v.id ? " is-on" : ""}`}
            onClick={() => openView(v)}
            // The chip shows the view's NAME; its tooltip says what the view is
            // made of, which is the one place a saved view's shape is visible
            // without applying it. Singulars because "1 filters" reads as a bug.
            title={countOf(v.columns.length, "column") + " · " + countOf(activeLeadFilters(v.filters).length, "filter")}
          >
            {v.name}
          </button>
        ))}

        <span className="crm-views__acts">
          {edited && <span className="crm-views__edited">edited</span>}
          {edited && activeView && (
            <button className="crm-linkact" onClick={updateView}>Update “{activeView.name}”</button>
          )}
          {edited && (
            <button className="crm-linkact" onClick={() => applyViewState(baseline)}>
              {activeView ? "Revert" : "Clear all"}
            </button>
          )}
          {edited && views.length < MAX_VIEWS && (
            <span className="crm-anchor">
              <button className="crm-linkact" onClick={() => togglePanel("save")}>Save as new view</button>
              {panel === "save" && (
                <div className="crm-pop crm-pop--save">
                  <h4>Name this view</h4>
                  <input
                    autoFocus
                    maxLength={MAX_VIEW_NAME}
                    placeholder="e.g. SQLs needing enrichment"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && createView()}
                  />
                  <div className="crm-pop__actions">
                    <button className="crm-btn crm-btn--dark" disabled={!newName.trim()} onClick={createView}>Save view</button>
                    <button className="crm-btn crm-btn--ghost" onClick={() => setPanel(null)}>Cancel</button>
                  </div>
                </div>
              )}
            </span>
          )}
          {edited && views.length >= MAX_VIEWS && (
            <span className="crm-hint">{MAX_VIEWS} views is the limit — delete one to save another.</span>
          )}
          {activeView && <button className="crm-linkact" onClick={deleteView}>Delete view</button>}
          {!views.length && !edited && (
            <span className="crm-hint">Filter the list or change its columns, then save it as a view.</span>
          )}
        </span>
      </div>

      <div className="crm-filters">
        {PRIMARY_LEAD_FILTERS.map((def) => {
          if (def.kind === "text") {
            return (
              <input
                key={def.key}
                className="crm-input"
                placeholder={def.placeholder}
                value={filters[def.key]}
                onChange={(e) => setFilter(def.key, e.target.value)}
              />
            );
          }
          if (def.multi) {
            return (
              <MultiFilter
                key={def.key}
                def={def}
                facets={facets}
                values={leadFilterValues(filters, def.key)}
                open={panel === `f:${def.key}`}
                onToggleOpen={() => togglePanel(`f:${def.key}`)}
                onPick={(v) => pickFilter(def.key, v)}
                onClear={() => clearFilter(def.key)}
              />
            );
          }
          // A window or a mode: one value, and a native select says so.
          return (
            <select key={def.key} className="crm-select" value={filters[def.key]} onChange={(e) => setFilter(def.key, e.target.value)}>
              <option value="">{def.all}</option>
              {filterOptions(def, facets).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          );
        })}

        <span className="crm-anchor">
          <button className="crm-btn crm-btn--secondary" onClick={() => togglePanel("filters")}>
            More filters{extras.length ? ` · ${extras.length}` : ""}
          </button>
          {panel === "filters" && (
            <div className="crm-pop crm-pop--filters">
              <h4>Filters</h4>
              {SECONDARY_LEAD_FILTERS.map((def) => {
                const values = leadFilterValues(filters, def.key);
                const options = filterOptions(def, facets, values);
                // Multi filters are checkbox groups inline rather than a second
                // popover hanging off this one: a popover inside a popover is a
                // thing you fight, and this panel already scrolls.
                if (def.multi) {
                  return (
                    <div key={def.key} className="crm-poprow2">
                      <span>
                        {def.label}
                        {values.length > 0 && <b className="crm-num"> · {values.length}</b>}
                        {values.length > 0 && (
                          <button className="crm-linkact crm-poprow2__clear" onClick={() => clearFilter(def.key)}>clear</button>
                        )}
                      </span>
                      <div className="crm-checklist">
                        {options.map(([v, l]) => (
                          <label key={v} className="crm-check">
                            <input type="checkbox" checked={values.includes(v)} onChange={() => pickFilter(def.key, v)} />
                            <span>{l}</span>
                          </label>
                        ))}
                        {!options.length && <div className="crm-hint">No values yet.</div>}
                      </div>
                    </div>
                  );
                }
                return (
                  <label key={def.key} className="crm-poprow2">
                    <span>{def.label}</span>
                    <select
                      className="crm-select"
                      value={filters[def.key]}
                      onChange={(e) => setFilter(def.key, e.target.value)}
                    >
                      <option value="">{def.all}</option>
                      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </label>
                );
              })}
              <div className="crm-pop__divider crm-pop__menu">
                <button
                  className="crm-linkact"
                  disabled={!extras.length}
                  onClick={() => patch({
                    filters: Object.fromEntries(Object.entries(filters).map(([k, v]) => {
                      const d = LEAD_FILTER_BY_KEY.get(k);
                      return [k, d?.primary ? v : (d?.multi ? [] : "")];
                    })),
                  })}
                >
                  Clear these filters
                </button>
                <button className="crm-btn crm-btn--secondary" onClick={() => setPanel(null)}>Done</button>
              </div>
            </div>
          )}
        </span>

        <span className="crm-anchor">
          <button className="crm-btn crm-btn--secondary" onClick={() => togglePanel("columns")}>
            Columns · {columns.length}
          </button>
          {panel === "columns" && (
            <div className="crm-pop crm-pop--cols">
              <h4>Shown — drag to reorder</h4>
              {columns.map((key) => {
                const def = LEAD_COLUMNS.find((c) => c.key === key);
                if (!def) return null;
                return (
                  <div
                    key={key}
                    className={`crm-colpick__row${drag.over === key && drag.from !== key ? " is-over" : ""}`}
                    onDragOver={(e) => { e.preventDefault(); setDrag((d) => ({ ...d, over: key })); }}
                    onDrop={(e) => { e.preventDefault(); dropColumn(key); }}
                  >
                    <Grip
                      onDragStart={() => setDrag({ from: key, over: null })}
                      onDragEnd={() => setDrag({ from: null, over: null })}
                    />
                    <span className="crm-colpick__name">{def.label}</span>
                    <button
                      className="crm-linkact"
                      disabled={columns.length === 1}
                      title={columns.length === 1 ? "The table keeps at least one column" : "Hide this column"}
                      onClick={() => toggleColumn(key)}
                    >
                      Hide
                    </button>
                  </div>
                );
              })}

              <div className="crm-pop__divider">
                <h4>Add a column</h4>
                {addable.length > 0 && (
                  <input placeholder="Search columns" value={colQuery} onChange={(e) => setColQuery(e.target.value)} />
                )}
                {LEAD_COLUMN_GROUPS.map(([group, title]) => {
                  const inGroup = matching.filter((c) => c.group === group);
                  if (!inGroup.length) return null;
                  return (
                    <div key={group} className="crm-colpick__group">
                      <div className="crm-colpick__grouphead">{title}</div>
                      {inGroup.map((c) => (
                        <button key={c.key} className="crm-colpick__add" onClick={() => toggleColumn(c.key)}>
                          + {c.label}
                        </button>
                      ))}
                    </div>
                  );
                })}
                {!addable.length && <div className="crm-hint">Every column is shown.</div>}
                {addable.length > 0 && !matching.length && (
                  <div className="crm-hint">No column matches “{colQuery.trim()}”.</div>
                )}
              </div>
              <div className="crm-pop__actions crm-pop__divider">
                <button className="crm-btn crm-btn--secondary" onClick={() => setPanel(null)}>Done</button>
              </div>
            </div>
          )}
        </span>

        {/* What "not a lead" has taken out. Only here when the list holds
            something: an empty list is not a control worth a button, and a full
            one is the only place the deletions can be seen or undone. */}
        {ignored.length > 0 && (
          <span className="crm-anchor">
            <button className="crm-btn crm-btn--secondary" onClick={() => togglePanel("ignored")}>
              Ignored · {ignored.length}
            </button>
            {panel === "ignored" && (
              <div className="crm-pop crm-pop--ignored">
                <h4>Ignored senders</h4>
                <div className="crm-hint">
                  Marked “not a lead”. Nothing from these addresses reaches the CRM — no contact, no email on a
                  timeline, no proposal — until you add the person by hand.
                </div>
                {ignored.map((s) => (
                  <div key={s.email} className="crm-poprow2">
                    <span>
                      <b>{s.name || s.email}</b>
                      {s.name && <span className="crm-hint"> {s.email}</span>}
                      <span className="crm-hint">
                        {[s.company_name, s.note, `ignored ${dateShort(s.created_at)}`].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <button className="crm-linkact" disabled={busy} onClick={() => unignore(s.email)}>
                      Stop ignoring
                    </button>
                  </div>
                ))}
                {/* The records are gone, and this says so rather than implying
                    an undo: un-ignoring lets the person back IN, it does not
                    bring the deleted contact and timeline back. */}
                <div className="crm-pop__divider">
                  <div className="crm-hint">
                    Stopping does not restore the deleted records — it only lets the address reach the CRM again.
                  </div>
                  <div className="crm-pop__actions">
                    <button className="crm-btn crm-btn--secondary" onClick={() => setPanel(null)}>Done</button>
                  </div>
                </div>
              </div>
            )}
          </span>
        )}

        {/* Secondary filters show as removable chips — a filter set from inside a
            popover is otherwise invisible, and an invisible filter is how you end
            up trusting a table that is hiding rows. */}
        {extras.map(([key, values]) => {
          const def = LEAD_FILTER_BY_KEY.get(key);
          return (
            <button
              key={key}
              className="crm-fchip"
              onClick={() => clearFilter(key)}
              // The chip names two values and counts the rest; the tooltip has
              // all of them, because a chip reading "+4" must still be able to
              // answer which four.
              title={`${def.label}: ${valuesTitle(def, values, facets)} — click to remove`}
            >
              {def.label}: {valuesLabel(def, values, facets)}
              <span aria-hidden="true">✕</span>
            </button>
          );
        })}
      </div>

      <div className="crm-leads">
        {error && <p className="crm-login__err" style={{ marginBottom: 10 }}>{error}</p>}

        {/* Bulk bar exists only while rows are checked. */}
        {selectedRows.length > 0 && (
          <div className="crm-bulkbar">
            <span className="crm-num">{selectedRows.length} selected</span>
            <span className="crm-bulkbar__sep">·</span>
            <select
              className="crm-select"
              value=""
              disabled={busy}
              onChange={(e) => e.target.value && changeStage(selectedRows, e.target.value)}
            >
              <option value="">Change stage</option>
              {STAGE_OPTIONS.map((s) => <option key={s} value={s}>{STAGE_PILLS[s].label}</option>)}
            </select>
            <span className="crm-bulkbar__sep">·</span>
            {/* The record's Change status… for the whole selection: every move a
                record has, each offered when it fits one of the companies. */}
            <span className="crm-statuswrap crm-statuswrap--bar" ref={status && status.key === "bulk" ? statusRef : undefined}>
              <button className="crm-bulkbar__action" disabled={busy} onClick={() => openStatus("bulk", selectedRows)}>Change status…</button>
              {statusMenu("bulk")}
            </span>
            <span className="crm-bulkbar__sep">·</span>
            <button className="crm-bulkbar__action" disabled={busy} onClick={() => markJunk(selectedRows)}>Mark junk</button>
            <span className="crm-bulkbar__sep">·</span>
            <button className="crm-bulkbar__action" disabled={busy} onClick={() => openDelete(selectedRows)}>Delete…</button>
            {selectedRows.length >= 2 && (
              <>
                <span className="crm-bulkbar__sep">·</span>
                <button className="crm-bulkbar__action" disabled={busy} onClick={() => openMerge(selectedRows)}>Merge…</button>
              </>
            )}
            <button className="crm-bulkbar__action crm-bulkbar__clear" onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        )}

        {/* The table keeps its columns on a phone and scrolls sideways inside this
            wrapper — a leads table narrowed into cards stops being the thing it
            is, which is a grid you scan down one column of. */}
        <div className="crm-tablewrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th className="crm-col--tight">
                <input type="checkbox" checked={allShownSelected} onChange={toggleAll} aria-label="Select all" />
              </th>
              {shown.map((col) => (
                <th key={col.key}>
                  {col.sort ? (
                    <button type="button" className="crm-th-sort" onClick={() => toggleSort(col.sort)}>
                      {col.label}
                      <span className="crm-th-sort__arrow" aria-hidden="true">
                        {sort.key === col.sort ? (sort.dir === "asc" ? "↑" : "↓") : ""}
                      </span>
                    </button>
                  ) : col.label}
                </th>
              ))}
              <th className="crm-col--menu" />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const exit = companyExitTag(c);
              // Opens the record HERE. /contacts/:id is still the record's own
              // address — a direct link, a new tab from the panel — but reaching
              // it by clicking a row cost the filters, the loaded page and the
              // scroll position every time.
              const open = () => (peekId && peekId !== c.id ? leaveRecord(() => setPeekId(c.id)) : setPeekId(c.id));
              return (
                <tr
                  key={c.id}
                  className={`crm-tr--link${selected.has(c.id) ? " crm-tr--selected" : ""}${peekId === c.id ? " crm-tr--open" : ""}${exit ? " crm-tr--disabled" : ""}`}
                  onClick={open}
                >
                  <td className="crm-col--tight" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggle(c.id)}
                      aria-label={`Select ${contactName(c)}`}
                    />
                  </td>
                  {shown.map((col) => (
                    <td key={col.key} className={col.cell.td || undefined}>{col.cell.render(c)}</td>
                  ))}
                  <td className="crm-col--menu" onClick={(e) => e.stopPropagation()}>
                    <span className="crm-statuswrap" ref={status && status.key === c.id ? statusRef : undefined}>
                      <button className="crm-row__menu" onClick={() => openStatus(c.id, [c])} aria-label="Change status">⋯</button>
                      {statusMenu(c.id)}
                    </span>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td className="crm-empty" colSpan={shown.length + 2}>
                  {counts.total ? "No leads match these filters." : "No leads yet. Add the first one with “New lead”."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>

        {/* The page, stated. The filters ran over every lead, so what is capped
            here is only how many of the matches are drawn — and a cap you can
            see and lift is a different thing from a cap that quietly decided
            what the numbers above meant. */}
        {rows.length < counts.matched && (
          <div className="crm-pager">
            <button className="crm-btn crm-btn--secondary" disabled={loadingMore} onClick={loadMore}>
              {loadingMore ? "Loading…" : `Load ${Math.min(PAGE, counts.matched - rows.length)} more`}
            </button>
            <span className="crm-hint crm-num">{rows.length} of {counts.matched} matches shown</span>
          </div>
        )}
      </div>

      {/* The record, overlaid on the table's right edge rather than sharing the
          width with it. Gabriel's ruling, and the reason it is the right one: the
          table is eight columns and giving up 660px of them reflows the list, so
          opening a row moves the row you just clicked. Nothing under here moves.

          Keyed on the contact so switching rows remounts rather than showing the
          previous person's timeline while the next one loads. */}
      {peekId && (
        <aside className="crm-peek crm-leads__peek">
          <ContactDetailPage
            key={peekId}
            contactId={peekId}
            embedded
            // Refreshed on the way out, and as soon as an email goes from it.
            onClose={closePeek}
            onChange={refreshRows}
            hostLabel="Leads"
            guardRef={recordGuard}
          />
        </aside>
      )}

      {/* The confirm for whatever Change status… or Delete… picked, over the page
          the way the record shows it on a phone. */}
      {ending && endingSpec && (
        <div className="rec">
          <div className="rec-dialog__backdrop" onClick={closeStatus}>
            <div className="rec-dialog" onClick={(e) => e.stopPropagation()}>
              <ConfirmCard
                key={ending.id}
                record={{ company: null, contact: null, act: null }}
                ops={statusOps}
                endingId={ending.id}
                preset={ending.preset}
                spec={endingSpec}
                run={(choice) => applyStatus(ending.id, choice)}
                onCancel={closeStatus}
              />
            </div>
          </div>
        </div>
      )}

      {merging && (
        <div className="rec">
          <div className="rec-dialog__backdrop" onClick={() => setMerging(null)}>
            <div className="rec-dialog" onClick={(e) => e.stopPropagation()}>
              <MergeCard preview={merging} run={applyMerge} onCancel={() => setMerging(null)} />
            </div>
          </div>
        </div>
      )}

      {showAdd && <NewLeadModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); reload(); }} />}
    </>
  );
}
